'use client';

import {
  Button,
  CircularProgress,
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { SqlEngine, SqlEngineStatus } from '@repo/api-gw-sdk';
import { useEffect, useRef, useState } from 'react';

import { useDAL } from '@/contexts/useDAL';

import { SqlEngineStatusCell } from '../sql-engines/sqlEngineStatusCell';
import { useWorkspace } from '../workspaceContext';

interface SqlEngineSelectorProps {
  selectedEngineId: string | null;
  onEngineSelected: (engineId: string | null) => void;
  disabled?: boolean;
  workspaceIdOverride?: string;
}

const POLL_INTERVAL_MS = 3000;

const isStarted = (status: SqlEngineStatus) => status === 'STARTED';
const canStart = (status: SqlEngineStatus) => status === 'STOPPED';
const isStartFailed = (status: SqlEngineStatus) => status === 'CREATE_FAILED';

export const SqlEngineSelector = ({
  selectedEngineId,
  onEngineSelected,
  disabled,
  workspaceIdOverride,
}: SqlEngineSelectorProps) => {
  const dal = useDAL();
  const workspace = useWorkspace();
  const workspaceId = workspaceIdOverride ?? workspace.workspaceId;
  const [startingEngineId, setStartingEngineId] = useState<string | null>(null);

  const {
    data: enginesData,
    isLoading,
    mutate: refreshEngines,
  } = dal.lakehouse.useListSqlEngines(workspaceId);

  const refreshRef = useRef(refreshEngines);

  useEffect(() => {
    refreshRef.current = refreshEngines;
  }, [refreshEngines]);

  // Poll for engine status updates when starting an engine
  useEffect(() => {
    if (!startingEngineId) return;

    const interval = setInterval(() => {
      void refreshRef.current();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [startingEngineId]);

  // Check if starting engine is now running or has failed
  useEffect(() => {
    if (!startingEngineId || !enginesData?.sqlEngines) return;

    const engine = enginesData.sqlEngines.find(
      (e: SqlEngine) => e.id === startingEngineId
    );
    if (engine) {
      if (isStarted(engine.status)) {
        // Engine started successfully - select it
        setStartingEngineId(null);
        onEngineSelected(engine.id);
      } else if (isStartFailed(engine.status)) {
        // Engine failed to start - clear the starting state
        setStartingEngineId(null);
      }
    }
  }, [enginesData, startingEngineId, onEngineSelected]);

  const engines = enginesData?.sqlEngines ?? [];
  const startedEngines = engines.filter((e: SqlEngine) => isStarted(e.status));
  const otherEngines = engines.filter((e: SqlEngine) => !isStarted(e.status));

  const handleStartEngine = async (e: React.MouseEvent, engineId: string) => {
    e.stopPropagation();
    e.preventDefault();
    setStartingEngineId(engineId);
    try {
      await dal.lakehouse.startSqlEngine(workspaceId!, engineId);
      void refreshEngines();
    } catch (err) {
      console.error('Failed to start engine:', err);
      setStartingEngineId(null);
    }
  };

  const selectedEngine = engines.find(
    (e: SqlEngine) => e.id === selectedEngineId
  );

  // Auto-select first running engine on mount
  useEffect(() => {
    if (!selectedEngineId && startedEngines.length > 0) {
      onEngineSelected(startedEngines[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engines.length]);

  const renderEngineItem = (engine: SqlEngine, showStartButton: boolean) => (
    <MenuItem
      key={engine.id}
      value={engine.id}
      disabled={!isStarted(engine.status) && startingEngineId !== engine.id}
      sx={{ py: '8px' }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        width='100%'
        gap='16px'
      >
        <Stack
          direction='row'
          alignItems='center'
          gap='16px'
          sx={{ minWidth: 0 }}
        >
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant='body1' noWrap>
              {engine.name}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction='row' alignItems='center' gap='8px'>
          <SqlEngineStatusCell status={engine.status} hideTooltip />
          {showStartButton && canStart(engine.status) && (
            <Button
              size='small'
              variant='outlined'
              onClick={(e) => void handleStartEngine(e, engine.id)}
              disabled={!!startingEngineId}
              startIcon={
                startingEngineId === engine.id ? (
                  <CircularProgress size={14} />
                ) : null
              }
              sx={{ ml: '8px' }}
            >
              {startingEngineId === engine.id ? 'Starting...' : 'Start'}
            </Button>
          )}
        </Stack>
      </Stack>
    </MenuItem>
  );

  return (
    <FormControl fullWidth size='small'>
      <Select
        value={selectedEngineId ?? ''}
        displayEmpty
        onChange={(e) => onEngineSelected(e.target.value || null)}
        disabled={disabled || isLoading}
        renderValue={() => {
          if (!selectedEngine)
            return (
              <Typography color='text.secondary'>Select engine</Typography>
            );
          return selectedEngine.name;
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: 'var(--mui-palette-background-paper)',
            },
          },
        }}
      >
        {isLoading && (
          <MenuItem disabled>
            <CircularProgress size={20} sx={{ mr: '8px' }} />
            Loading engines...
          </MenuItem>
        )}

        {!isLoading && engines.length === 0 && (
          <MenuItem disabled>
            <Typography color='text.secondary'>
              No SQL engines available. Create one first.
            </Typography>
          </MenuItem>
        )}

        {startedEngines.length > 0 && (
          <ListSubheader>Started Engines</ListSubheader>
        )}
        {startedEngines.map((engine: SqlEngine) =>
          renderEngineItem(engine, false)
        )}

        {otherEngines.length > 0 && (
          <ListSubheader>Other Engines</ListSubheader>
        )}
        {otherEngines.map((engine: SqlEngine) =>
          renderEngineItem(engine, true)
        )}
      </Select>
    </FormControl>
  );
};
